import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { memo } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useReviewsWorkspaceAccess } from "./use-reviews-workspace-access";

const ReviewsWorkspaceAccessGuard = memo(
  function ReviewsWorkspaceAccessGuard() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const { isPending, workspaceAvailable } = useReviewsWorkspaceAccess();

    if (isPending) {
      return null;
    }
    if (!workspaceAvailable) {
      return <Navigate replace to={routes.marketingBaseUrl} />;
    }
    return <Outlet />;
  },
);

const NativeReviewsAccessGuard = memo(function NativeReviewsAccessGuard() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { nativeReviewsEnabled } = useReviewsWorkspaceAccess();

  if (!nativeReviewsEnabled) {
    return <Navigate replace to={routes.getReviewsSubRoute("all-reviews")} />;
  }
  return <Outlet />;
});

const ReviewsIndexRoute = memo(function ReviewsIndexRoute() {
  const { nativeReviewsEnabled } = useReviewsWorkspaceAccess();
  return (
    <Navigate replace to={nativeReviewsEnabled ? "dashboard" : "all-reviews"} />
  );
});

export const reviewsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Reviews", breadcrumbNavigate: false },
    element: <ReviewsWorkspaceAccessGuard />,
    children: [
      { index: true, element: <ReviewsIndexRoute /> },
      {
        path: "all-reviews",
        handle: { breadcrumb: "All reviews" },
        async lazy() {
          const { AllReviews } =
            await import("./all-reviews/all-reviews-table");
          return { Component: AllReviews };
        },
      },
      {
        path: "products",
        handle: { breadcrumb: "Products" },
        async lazy() {
          const { Products } = await import("./products/review-products-table");
          return { Component: Products };
        },
      },
      {
        element: <NativeReviewsAccessGuard />,
        children: [
          {
            path: "dashboard",
            handle: { breadcrumb: "Dashboard" },
            async lazy() {
              const { Dashboard } = await import("./dashboard/dashboard");
              return { Component: Dashboard };
            },
          },
          {
            handle: { breadcrumb: "Response" },
            path: "reviews-response",
            async lazy() {
              const { Response } =
                await import("./form-and-response/response/response");
              return { Component: Response };
            },
          },
          {
            handle: { breadcrumb: "Settings" },
            path: "settings",
            async lazy() {
              const { Settings } = await import("./settings/settings");
              return { Component: Settings };
            },
          },
          {
            handle: { breadcrumb: "Displays & widgets" },
            path: "reviews-displays-widgets",
            children: [
              {
                index: true,
                async lazy() {
                  const { DisplaysWidgets } =
                    await import("./form-and-response/displays-widgets");
                  return { Component: DisplaysWidgets };
                },
              },
              {
                path: "review-form",
                handle: { breadcrumb: "Edit" },
                async lazy() {
                  const { FormBuilder } =
                    await import("./form-and-response/form-builder/form-builder");
                  return { Component: FormBuilder };
                },
              },
              {
                path: "star-rating-widget",
                handle: { breadcrumb: "Star rating widget" },
                async lazy() {
                  const { StarRatingWidgetBuilder } =
                    await import("./form-and-response/star-rating-widget/star-rating-widget-builder");
                  return { Component: StarRatingWidgetBuilder };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
